import { Form, Select as AntSelect } from 'antd'
import type { FormInstance } from 'antd'
import { useEffect, useRef } from 'react'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import dayjs from 'dayjs'

export interface UnitFormValues {
  id?: string
  name: string
  code: string
  level: number
  phoneNumber?: string
  startDate: dayjs.Dayjs | null
  endDate?: dayjs.Dayjs | null
  fullNameAdmin: string
  phoneNumberAdmin?: string
  personalEmailAdmin: string
  note?: string
  email?: string
}

interface UnitFormProps {
  form: FormInstance<UnitFormValues>
  mode: 'create' | 'update'
  onSubmit: (values: UnitFormValues) => void
}

const PILL_INPUT =
  'rounded-full !bg-white disabled:!bg-gray-100 disabled:hover:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed'
const TEXTAREA_INPUT =
  'rounded-2xl py-3 !bg-white disabled:!bg-gray-100 disabled:hover:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed'

export function UnitForm({ form, mode, onSubmit }: UnitFormProps) {
  const initialDateRef = useRef<{
    startDate?: dayjs.Dayjs | null
    endDate?: dayjs.Dayjs | null
  } | null>(null)

  const startDateVal = Form.useWatch('startDate', form)
  const endDateVal = Form.useWatch('endDate', form)

  useEffect(() => {
    if (
      mode === 'update' &&
      (startDateVal || endDateVal) &&
      !initialDateRef.current
    ) {
      initialDateRef.current = {
        startDate: startDateVal,
        endDate: endDateVal,
      }
    }
  }, [mode, startDateVal, endDateVal])

  const hasDateChanged = (
    currentVal?: dayjs.Dayjs | null,
    initialVal?: dayjs.Dayjs | null,
  ) => {
    if (mode !== 'update') return true
    if (!currentVal && !initialVal) return false
    if (!currentVal || !initialVal) return true
    return !currentVal.isSame(initialVal, 'day')
  }

  const handleFinish = (values: UnitFormValues) => {
    const trimmedValues = Object.fromEntries(
      Object.entries(values).map(([key, val]) => [
        key,
        typeof val === 'string' ? val.trim() : val,
      ]),
    ) as UnitFormValues

    onSubmit(trimmedValues)
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={{ level: 2 }}
      requiredMark={(label, info) => (
        <>
          {label}
          {info.required && <span className="text-destructive ml-1">*</span>}
        </>
      )}
      className="space-y-6"
    >
      {/* Thông tin chung */}
      <div className="rounded-xl border border-solid border-gray-300 px-5">
        <h3 className="mb-4 mt-4 text-lg font-semibold">Thông tin chung</h3>

        <Form.Item
          name="name"
          label="Tên đơn vị"
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Vui lòng nhập tên đơn vị',
            },
          ]}
        >
          <Input
            placeholder="Nhập tên đơn vị"
            size="large"
            className={PILL_INPUT}
          />
        </Form.Item>

        <Form.Item
          name="code"
          label="Mã đơn vị"
          rules={[
            { required: true, message: 'Vui lòng nhập mã đơn vị' },
            {
              pattern: /^[a-zA-Z0-9._-]+$/,
              message:
                'Mã đơn vị chỉ được chứa chữ cái, số, dấu chấm, gạch ngang và gạch dưới',
            },
          ]}
        >
          <Input
            placeholder="Nhập mã đơn vị"
            size="large"
            className={PILL_INPUT}
          />
        </Form.Item>

        <Form.Item
          name="level"
          label="Cấp"
          rules={[{ required: true, message: 'Vui lòng chọn cấp đơn vị' }]}
        >
          <AntSelect
            allowClear
            placeholder="Chọn cấp"
            size="large"
            className="bg-white"
            options={[
              { value: 1, label: 'Cấp Tỉnh/Thành phố' },
              { value: 2, label: 'Cấp Phường/Xã' },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="phoneNumber"
          label="Số điện thoại"
          normalize={(value) => (value || '').replace(/[a-zA-Z]/g, '')}
          rules={[
            {
              pattern: /^[0-9+()\s-]+$/,
              message: 'Số điện thoại không hợp lệ',
            },
          ]}
        >
          <Input
            placeholder="Nhập số điện thoại"
            size="large"
            className={PILL_INPUT}
          />
        </Form.Item>

        <Form.Item
          name="startDate"
          label="Ngày bắt đầu"
          dependencies={['endDate']}
          getValueFromEvent={(date: dayjs.Dayjs | null) => {
            if (!date) return null
            return date.startOf('day')
          }}
          rules={[
            { required: true, message: 'Vui lòng chọn ngày bắt đầu' },
            () => ({
              validator(_, value) {
                if (!value) return Promise.resolve()
                const today = dayjs().startOf('day')
                const isChanged = hasDateChanged(
                  value,
                  initialDateRef.current?.startDate,
                )

                if (isChanged && value.isBefore(today, 'day')) {
                  return Promise.reject(
                    new Error('Ngày bắt đầu không được nhỏ hơn ngày hiện tại'),
                  )
                }
                return Promise.resolve()
              },
            }),
          ]}
        >
          <DatePicker
            placeholder="DD/MM/YYYY"
            format="DD/MM/YYYY"
            size="large"
            className={`w-full ${PILL_INPUT}`}
          />
        </Form.Item>

        <Form.Item
          name="endDate"
          label="Ngày kết thúc"
          dependencies={['startDate']}
          getValueFromEvent={(date: dayjs.Dayjs | null) => {
            if (!date) return null
            return date.endOf('day').millisecond(0)
          }}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value) return Promise.resolve()
                const today = dayjs().startOf('day')
                const isChanged = hasDateChanged(
                  value,
                  initialDateRef.current?.endDate,
                )
                if (isChanged && value.isBefore(today, 'day')) {
                  return Promise.reject(
                    new Error('Ngày kết thúc không được nhỏ hơn ngày hiện tại'),
                  )
                }
                const startDate = getFieldValue('startDate')
                if (startDate && value.isBefore(startDate, 'day')) {
                  return Promise.reject(
                    new Error('Ngày kết thúc không được nhỏ hơn ngày bắt đầu'),
                  )
                }
                return Promise.resolve()
              },
            }),
          ]}
        >
          <DatePicker
            placeholder="DD/MM/YYYY"
            format="DD/MM/YYYY"
            size="large"
            className={`w-full ${PILL_INPUT}`}
          />
        </Form.Item>

        <Form.Item name="note" label="Ghi chú">
          <Input.TextArea
            placeholder="Nhập ghi chú"
            size="large"
            autoSize={{ minRows: 4, maxRows: 6 }}
            className={TEXTAREA_INPUT}
          />
        </Form.Item>
      </div>

      {/* Người Đại Diện */}
      <div className="rounded-xl border border-solid border-gray-300 px-5">
        <h3 className="mb-4 mt-4 text-lg font-semibold">Người Đại Diện</h3>

        <Form.Item
          name="fullNameAdmin"
          label="Người đại diện"
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Vui lòng nhập họ tên người đại diện',
            },
          ]}
        >
          <Input
            placeholder="Nhập họ tên"
            size="large"
            className={PILL_INPUT}
          />
        </Form.Item>

        <Form.Item
          name="phoneNumberAdmin"
          label="Số điện thoại"
          normalize={(value) => (value || '').replace(/[a-zA-Z]/g, '')}
          rules={[
            {
              pattern: /^[0-9+()\s-]+$/,
              message: 'Số điện thoại không hợp lệ',
            },
          ]}
        >
          <Input
            placeholder="Nhập số điện thoại người đại diện"
            size="large"
            className={PILL_INPUT}
          />
        </Form.Item>

        <Form.Item
          name="personalEmailAdmin"
          label="Email"
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Vui lòng nhập email người đại diện',
            },
            { type: 'email', message: 'Email không hợp lệ' },
          ]}
        >
          <Input
            placeholder="Nhập email người đại diện"
            size="large"
            className={PILL_INPUT}
          />
        </Form.Item>
      </div>

      {/* Thông tin tài khoản */}
      <div className="rounded-xl border border-solid border-gray-300 px-5">
        <h3 className="mb-4 mt-4 text-lg font-semibold">Thông tin tài khoản</h3>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Vui lòng nhập email',
            },
            { type: 'email', message: 'Email không hợp lệ' },
          ]}
        >
          <Input
            placeholder="Nhập email"
            size="large"
            disabled={mode === 'update'}
            className={PILL_INPUT}
          />
        </Form.Item>
      </div>
    </Form>
  )
}
